import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
  useTheme,
  useMediaQuery,
  Alert,
  AlertTitle,
  ButtonBase,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingBag as ShoppingBagIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  BarChart as BarChartIcon,
  PersonOutline as PersonIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Description as FiscalIcon,
  Security as CertificateIcon,
  Replay as RecurringIcon,
  AccountBalanceWallet as BankIcon,
  WhatsApp as WhatsAppIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import Logo from "./Logo";
import { brand } from "../theme";

const drawerWidth = 260;

export default function LayoutMUI({ children, user, onLogout }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openSections, setOpenSections] = useState({
    principal: true,
    estoque: true,
    vendas: true,
    cadastros: true,
    gestao: true,
    fiscal: true,
    integracoes: true,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Somente supervisor/admin
  const isSupervisorOrAdmin =
    user?.role === "company_supervisor" || user?.role === "system_admin";

  const navSections = [
    {
      id: "principal",
      title: "PRINCIPAL",
      items: [
        { path: "/dashboard", label: "Dashboard", icon: <DashboardIcon /> },
        { path: "/pdv", label: "PDV", icon: <ShoppingCartIcon /> },
      ],
    },
    {
      id: "estoque",
      title: "ESTOQUE",
      items: [
        { path: "/products", label: "Produtos", icon: <InventoryIcon /> },
        { path: "/stock-movements", label: "Movimentações", icon: <TrendingUpIcon /> },
        { path: "/purchases", label: "Compras", icon: <ShoppingBagIcon /> },
        { path: "/xml-import", label: "Lançando (XML)", icon: <UploadIcon /> },
      ],
    },
    {
      id: "vendas",
      title: "VENDAS",
      items: [
        { path: "/sales", label: "Vendas", icon: <ReceiptIcon /> },
        { path: "/customers", label: "Clientes", icon: <PeopleIcon /> },
        { path: "/cashier", label: "Caixa", icon: <AccountBalanceIcon /> },
      ],
    },
    // Nova seção CADASTROS – somente supervisor/admin
    ...(isSupervisorOrAdmin
      ? [
        {
          id: "cadastros",
          title: "CADASTROS",
          items: [
            { path: "/suppliers", label: "Fornecedores", icon: <InventoryIcon /> },
            { path: "/categories", label: "Categorias", icon: <InventoryIcon /> },
          ],
        },
      ]
      : []),
    // Seção GESTÃO – já existente, também somente supervisor/admin
    ...(isSupervisorOrAdmin
      ? [
        {
          id: "gestao",
          title: "GESTÃO",
          items: [
            { path: "/finance", label: "Financeiro", icon: <AttachMoneyIcon /> },
            { path: "/billing", label: "Mensalidade", icon: <ReceiptIcon /> },
            { path: "/recurring", label: "Recorrências", icon: <RecurringIcon /> },
            { path: "/bank", label: "Conciliação Bancária", icon: <BankIcon /> },
            { path: "/reports", label: "Relatórios", icon: <BarChartIcon /> },
            { path: "/users", label: "Usuários", icon: <PersonIcon /> },
          ],
        },
      ]
      : []),
    // Seção FISCAL
    ...(isSupervisorOrAdmin
      ? [
        {
          id: "fiscal",
          title: "FISCAL",
          items: [
            { path: "/fiscal", label: "Notas Fiscais", icon: <FiscalIcon /> },
            { path: "/certificates", label: "Certificados", icon: <CertificateIcon /> },
            { path: "/dfe", label: "Busca NF-e", icon: <SearchIcon /> },
          ],
        },
      ]
      : []),
    // Seção INTEGRAÇÕES
    ...(isSupervisorOrAdmin
      ? [
        {
          id: "integracoes",
          title: "INTEGRAÇÕES",
          items: [
            { path: "/whatsapp", label: "WhatsApp", icon: <WhatsAppIcon /> },
            { path: "/company-profile", label: "Dados da Empresa", icon: <SettingsIcon /> },
            { path: "/settings", label: "Configurações", icon: <SettingsIcon /> },
          ],
        },
      ]
      : []),
  ];

  const drawer = (
    <Box>
      {/* Header do Drawer */}
      <Toolbar
        disableGutters
        sx={{
          px: 2.5,
          minHeight: 72,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <ButtonBase
          onClick={() => {
            navigate("/");
            if (isMobile) setMobileOpen(false);
          }}
          aria-label="Ir para o início"
          sx={{
            width: "-webkit-fill-available",
            textAlign: "center",
            borderRadius: 1,
            py: 0.5,
            px: 1.5,
            bgcolor: "#fcfbfc",
            justifyContent: "center",
            transition: "transform 0.18s ease, box-shadow 0.18s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: "0 10px 24px rgba(3,20,38,0.22)",
            },
            "&:focus-visible": {
              outline: `2px solid ${brand.primaryLight}`,
              outlineOffset: 3,
            },
          }}
        >
          <Logo variant="full" size={44} />
        </ButtonBase>
      </Toolbar>

      {/* Navigation */}
      <List sx={{ px: 1, py: 1 }}>
        {navSections.map((section) => (
          <Box key={section.id} sx={{ mb: 0.5 }}>
            {/* Section Header */}
            <ListItemButton
              onClick={() => toggleSection(section.id)}
              sx={{
                borderRadius: 1,
                py: 0.5,
                px: 1.5,
                color: "rgba(226,238,249,0.70)",
                "&:hover": {
                  bgcolor: "rgba(85,199,255,0.08)",
                },
              }}
            >
              <ListItemText
                primary={section.title}
                primaryTypographyProps={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "rgba(226,238,249,0.58)",
                  letterSpacing: "0.5px",
                }}
              />
              {openSections[section.id] ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </ListItemButton>

            {/* Section Items */}
            <Collapse in={openSections[section.id]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          navigate(item.path);
                          if (isMobile) setMobileOpen(false);
                        }}
                        sx={{
                          borderRadius: 1,
                          mx: 0.5,
                          mb: 0.25,
                          bgcolor: isActive ? "rgba(16,151,230,0.18)" : "transparent",
                          background: isActive
                            ? `linear-gradient(135deg, ${brand.primaryLight} 0%, ${brand.primary} 48%, ${brand.primaryDark} 100%)`
                            : "transparent",
                          color: isActive ? "white" : "rgba(226,238,249,0.82)",
                          boxShadow: isActive ? "0 12px 28px rgba(16,151,230,0.24)" : "none",
                          "&:hover": {
                            bgcolor: isActive ? "primary.dark" : "rgba(85,199,255,0.10)",
                            color: "white",
                            "& .MuiListItemIcon-root": {
                              color: "white",
                              bgcolor: isActive ? "rgba(255,255,255,0.18)" : "rgba(85,199,255,0.14)",
                            },
                          },
                        }}
                        data-testid={`nav-${item.label
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        <ListItemIcon
                          sx={{
                            color: isActive ? "white" : brand.primaryLight,
                            minWidth: 40,
                            width: 30,
                            height: 30,
                            borderRadius: 1,
                            mr: 1,
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: isActive ? "rgba(255,255,255,0.18)" : "rgba(85,199,255,0.08)",
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: "0.9rem",
                            fontWeight: isActive ? 700 : 500,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(14px)",
          color: "text.primary",
          boxShadow: "none",
          borderBottom: `1px solid ${brand.surface}`,
        }}
      >
        <Toolbar sx={{ px: 3 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          {/* User Menu */}
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
              onClick={handleMenuOpen}
            >
              <Typography variant="body2">
                {user?.name}
              </Typography>
              <IconButton size="small">
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "primary.main",
                    background: `linear-gradient(135deg, ${brand.primaryLight}, ${brand.primaryDark})`,
                  }}
                >
                  {user?.name?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Sair</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: brand.navy,
              color: "white",
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: brand.navy,
              color: "white",
              borderRight: "1px solid rgba(85,199,255,0.16)",
              boxShadow: "14px 0 34px rgba(3,20,38,0.13)",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          bgcolor: "background.default",
          background:
            "radial-gradient(900px 360px at 88% -120px, rgba(16,151,230,0.11), transparent 60%), linear-gradient(180deg, #f8fbff 0%, #eef5fb 100%)",
        }}
      >
        <Toolbar />
        {user?.payment_warning?.show && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Mensalidade em atraso</AlertTitle>
            {user.payment_warning.message}
          </Alert>
        )}
        {children}
      </Box>
    </Box>
  );
}
